/**
 * Supervisão · Período (spec 071): os números do período, a equipe e a lista
 * de conversas (o histórico). As definições são as da tabela da spec; cada uma
 * está no (i) do número. Tela e CSV saem das mesmas colunas.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock3,
  Download,
  Inbox,
  MessageSquareReply,
  Star,
  Timer,
  UserCheck,
} from 'lucide-react';
import {
  capitalize,
  type AttendanceByAgent,
  type AttendanceByDesk,
  type AttendanceMetric,
  type AttendancePeriod,
  type AttendancePeriodChat,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { downloadCsv } from '@/features/reports/report-csv';
import { attendancePeriodQuery } from './supervision-queries';
import { LEVEL_TEXT, formatSeconds, formatWhen, waitLevel } from './supervision-format';
import {
  PERIOD_PRESET_LABELS,
  effectivePeriod,
  localDay,
  matchingPreset,
  presetPeriod,
  type PeriodPreset,
  type SupervisionSearch,
} from './supervision-search';
import { InfoTip, Section, StatTile, TH } from './supervision-ui';

const TH_NUM = 'h-9 px-4 text-right text-[13px] font-semibold whitespace-nowrap';
const TD_NUM = 'px-4 text-right tabular-nums whitespace-nowrap';
const PAGE = 50;

const DEF = {
  opened:
    'Chegaram à equipe no período: transferidas pelo bot, a primeira mensagem do cliente, ou iniciadas pela equipe (a "Nova conversa" ou o modelo mandado pelo atendente). Conversa só com mensagem do sistema, como a do disparo, não conta.',
  closed:
    'Encerradas no período. Sem atendimento: encerradas sem que ninguém da equipe as assumisse ou encerrasse.',
  wait: 'Da entrada na fila até a primeira vez que alguém da equipe assumiu. Das conversas que entraram no período; as iniciadas pela equipe ficam de fora.',
  firstResponse:
    'Da entrada na fila até a primeira mensagem de alguém da equipe para o cliente. Mensagem do bot, do sistema e nota interna não contam.',
  response:
    'Cada vez que o cliente escreve depois da última resposta, até a próxima resposta da equipe. A média das respostas dadas no período.',
  service:
    'Da primeira vez que alguém assumiu até o encerramento. Das conversas encerradas no período.',
  csat:
    'A nota que o cliente deu, de 1 a 5, na pesquisa enviada ao finalizar a conversa pelo WhatsApp (ligada por número, no Bot do número). Das conversas encerradas no período que receberam nota.',
};

/** "4,6" — a média com uma casa, no formato do Brasil */
const csatValue = (avg: number | null): string => (avg === null ? '—' : avg.toFixed(1).replace('.', ','));

export function SupervisionPeriod({
  search,
  deskIds,
  onPeriod,
}: {
  search: SupervisionSearch;
  deskIds: number[] | undefined;
  onPeriod: (start: string, end: string) => void;
}) {
  const today = localDay(new Date());
  const period = effectivePeriod(search, today);
  const preset = matchingPreset(period, today);
  const q = useQuery({ ...attendancePeriodQuery(period, deskIds), staleTime: 60_000 });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="flex items-center gap-0.5 rounded-md bg-muted p-0.5"
          role="group"
          aria-label="Período"
        >
          {(Object.keys(PERIOD_PRESET_LABELS) as PeriodPreset[]).map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={preset === p}
              onClick={() => {
                const x = presetPeriod(p, today);
                onPeriod(x.start, x.end);
              }}
              className={cn(
                'h-8 rounded-[5px] px-3 text-[13px] font-medium transition-colors',
                preset === p
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {PERIOD_PRESET_LABELS[p]}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          De
          <Input
            type="date"
            className="h-8 w-[9.5rem]"
            value={period.start}
            max={today}
            onChange={(e) => e.target.value && onPeriod(e.target.value, period.end)}
          />
        </label>
        <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          até
          <Input
            type="date"
            className="h-8 w-[9.5rem]"
            value={period.end}
            max={today}
            onChange={(e) => e.target.value && onPeriod(period.start, e.target.value)}
          />
        </label>
        {q.isFetching ? <Spinner className="size-4" /> : null}
      </div>

      {q.isPending ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" aria-busy>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : q.isError ? (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-destructive">
          Não foi possível carregar o período. Tente de novo.
        </p>
      ) : (
        <PeriodBody data={q.data} period={period} />
      )}
    </div>
  );
}

function avg(m: AttendanceMetric) {
  return formatSeconds(m.avgSeconds);
}

function PeriodBody({
  data,
  period,
}: {
  data: AttendancePeriod;
  period: { start: string; end: string };
}) {
  const t = data.totals;
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={Inbox}
          label="Abertas"
          value={t.opened}
          detail={`${t.openedByTeam} iniciadas pela equipe`}
          info={DEF.opened}
        />
        <StatTile
          icon={CheckCircle2}
          label="Encerradas"
          value={t.closed}
          tone={t.closedWithoutAttendance > 0 ? LEVEL_TEXT.warn : undefined}
          detail={`${t.closedWithoutAttendance} sem atendimento`}
          info={DEF.closed}
        />
        <StatTile
          icon={Clock3}
          label="Espera na fila"
          value={avg(t.wait)}
          tone={t.wait.avgSeconds !== null ? LEVEL_TEXT[waitLevel(t.wait.avgSeconds)] : undefined}
          detail={metricDetail(t.wait)}
          info={DEF.wait}
        />
        <StatTile
          icon={MessageSquareReply}
          label="1ª resposta"
          value={avg(t.firstResponse)}
          tone={
            t.firstResponse.avgSeconds !== null
              ? LEVEL_TEXT[waitLevel(t.firstResponse.avgSeconds)]
              : undefined
          }
          detail={metricDetail(t.firstResponse)}
          info={DEF.firstResponse}
        />
        <StatTile
          icon={Timer}
          label="Tempo de resposta"
          value={avg(t.response)}
          tone={
            t.response.avgSeconds !== null
              ? LEVEL_TEXT[waitLevel(t.response.avgSeconds)]
              : undefined
          }
          detail={metricDetail(t.response, 'respostas')}
          info={DEF.response}
        />
        <StatTile
          icon={UserCheck}
          label="Atendimento"
          value={avg(t.service)}
          detail={metricDetail(t.service)}
          info={DEF.service}
        />
        <StatTile
          icon={Star}
          label="Satisfação"
          value={t.csat.avg === null ? '—' : `${csatValue(t.csat.avg)} de 5`}
          detail={t.csat.count === 1 ? '1 avaliação' : `${t.csat.count} avaliações`}
          info={DEF.csat}
        />
      </div>

      <DaysChart data={data} />

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <ByDesk rows={data.byDesk} />
        <Reasons data={data} />
      </div>

      <ByAgent rows={data.byAgent} period={period} />
      <Chats data={data} period={period} />
    </>
  );
}

function metricDetail(m: AttendanceMetric, unit = 'conversas'): string {
  if (m.count === 0) return 'sem medida no período';
  return `máx. ${formatSeconds(m.maxSeconds)} · ${m.count} ${unit}`;
}

function DaysChart({ data }: { data: AttendancePeriod }) {
  const max = Math.max(1, ...data.days.map((d) => Math.max(d.opened, d.closed)));
  return (
    <Section title="Abertas e encerradas por dia" hint="Dias de São Paulo">
      <div className="px-4 pt-4 pb-2">
        <div
          className="flex h-32 items-end gap-[2px]"
          role="img"
          aria-label="Abertas e encerradas por dia"
        >
          {data.days.map((d) => (
            <div
              key={d.day}
              className="flex h-full min-w-0 flex-1 items-end justify-center gap-px"
              title={`${dayLabel(d.day)}: ${d.opened} abertas, ${d.closed} encerradas${d.closedWithoutAttendance ? `, ${d.closedWithoutAttendance} sem atendimento` : ''}`}
            >
              <div
                className="w-1/2 max-w-3 rounded-t-sm bg-primary/70"
                style={{ height: `${(d.opened / max) * 100}%` }}
              />
              <div
                className="w-1/2 max-w-3 rounded-t-sm bg-[#52C41A]/70"
                style={{ height: `${(d.closed / max) * 100}%` }}
              />
            </div>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
          <span>{dayLabel(data.days[0]?.day)}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="size-2 rounded-sm bg-primary/70" /> abertas
            </span>
            <span className="flex items-center gap-1">
              <span className="size-2 rounded-sm bg-[#52C41A]/70" /> encerradas
            </span>
          </span>
          <span>{dayLabel(data.days[data.days.length - 1]?.day)}</span>
        </div>
      </div>
    </Section>
  );
}

function dayLabel(day: string | undefined): string {
  if (!day) return '';
  const [, m, d] = day.split('-');
  return `${d}/${m}`;
}

function MetricCells({ m }: { m: AttendanceMetric }) {
  return (
    <td
      className={cn(TD_NUM, m.avgSeconds !== null && LEVEL_TEXT[waitLevel(m.avgSeconds)])}
      title={m.count ? `máx. ${formatSeconds(m.maxSeconds)} · ${m.count}` : undefined}
    >
      {formatSeconds(m.avgSeconds)}
    </td>
  );
}

function ByDesk({ rows }: { rows: AttendanceByDesk[] }) {
  return (
    <Section title="Por mesa" count={rows.length} empty="Nenhuma conversa no período.">
      {rows.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted">
              <th className={TH}>Mesa</th>
              <th className={TH_NUM}>Abertas</th>
              <th className={TH_NUM}>Encerradas</th>
              <th className={TH_NUM}>
                Sem atend.
                <InfoTip>{DEF.closed}</InfoTip>
              </th>
              <th className={TH_NUM}>
                Espera
                <InfoTip>{DEF.wait}</InfoTip>
              </th>
              <th className={TH_NUM}>
                1ª resp.
                <InfoTip>{DEF.firstResponse}</InfoTip>
              </th>
              <th className={TH_NUM}>
                Resposta
                <InfoTip>{DEF.response}</InfoTip>
              </th>
              <th className={TH_NUM}>
                Atend.
                <InfoTip>{DEF.service}</InfoTip>
              </th>
              <th className={TH_NUM}>
                Nota
                <InfoTip>{DEF.csat}</InfoTip>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr
                key={String(d.deskId)}
                className="h-10 border-b border-divider last:border-0 hover:bg-accent/60"
              >
                <td className="max-w-48 truncate px-4 font-medium">{d.deskName}</td>
                <td className={TD_NUM}>{d.opened}</td>
                <td className={TD_NUM}>{d.closed}</td>
                <td className={cn(TD_NUM, d.closedWithoutAttendance > 0 && LEVEL_TEXT.warn)}>
                  {d.closedWithoutAttendance}
                </td>
                <MetricCells m={d.wait} />
                <MetricCells m={d.firstResponse} />
                <MetricCells m={d.response} />
                <td className={TD_NUM}>{formatSeconds(d.service.avgSeconds)}</td>
                <td className={TD_NUM} title={`${d.csat.count} avaliações`}>
                  {csatValue(d.csat.avg)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Section>
  );
}

function Reasons({ data }: { data: AttendancePeriod }) {
  const total = data.reasons.reduce((s, r) => s + r.count, 0);
  return (
    <Section
      title="Motivos de encerramento"
      count={data.reasons.length}
      empty="Nenhuma conversa encerrada no período."
    >
      {data.reasons.length > 0 ? (
        <ul className="space-y-2 px-4 py-3">
          {data.reasons.map((r) => (
            <li key={`${r.reasonId}-${r.reasonName}`} className="text-sm">
              <div className="flex justify-between gap-2">
                <span className="truncate">{r.reasonName}</span>
                <span className="tabular-nums text-muted-foreground">{r.count}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-muted">
                <div
                  className="h-1.5 rounded-full bg-primary/60"
                  style={{ width: `${(r.count / total) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

function agentCsvRows(rows: AttendanceByAgent[]): (string | number)[][] {
  return rows.map((a) => [
    a.userName,
    a.attended,
    a.closed,
    csvSeconds(a.firstResponse.avgSeconds),
    csvSeconds(a.response.avgSeconds),
    a.response.count,
    csvSeconds(a.service.avgSeconds),
    a.csat.avg === null ? '' : csatValue(a.csat.avg),
    a.csat.count,
  ]);
}

/** segundos → minutos com vírgula, para o Excel pt-BR somar e fazer média */
function csvSeconds(s: number | null): string {
  return s === null ? '' : (s / 60).toFixed(1).replace('.', ',');
}

function ByAgent({
  rows,
  period,
}: {
  rows: AttendanceByAgent[];
  period: { start: string; end: string };
}) {
  return (
    <Section
      title="Por atendente"
      count={rows.length}
      empty="Ninguém da equipe atendeu conversas no período."
      actions={
        rows.length > 0 ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={() =>
              downloadCsv(
                `atendimento-equipe-${period.start}-a-${period.end}.csv`,
                [
                  'Atendente',
                  'Atendidas',
                  'Encerradas',
                  '1ª resposta média (min)',
                  'Resposta média (min)',
                  'Respostas',
                  'Atendimento médio (min)',
                  'Nota média',
                  'Avaliações',
                ],
                agentCsvRows(rows)
              )
            }
          >
            <Download className="size-3.5" /> CSV
          </Button>
        ) : null
      }
    >
      {rows.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted">
              <th className={TH}>Atendente</th>
              <th className={TH_NUM}>
                Atendidas
                <InfoTip>Conversas do período que a pessoa teve na mão em algum momento.</InfoTip>
              </th>
              <th className={TH_NUM}>
                Encerradas
                <InfoTip>Conversas encerradas por ela no período.</InfoTip>
              </th>
              <th className={TH_NUM}>
                1ª resposta
                <InfoTip>Das conversas em que foi ela quem deu a primeira resposta.</InfoTip>
              </th>
              <th className={TH_NUM}>
                Resposta
                <InfoTip>{DEF.response} Contada para quem respondeu.</InfoTip>
              </th>
              <th className={TH_NUM}>Respostas</th>
              <th className={TH_NUM}>
                Atendimento
                <InfoTip>
                  Das conversas que ela encerrou: da primeira posse até o encerramento.
                </InfoTip>
              </th>
              <th className={TH_NUM}>
                Nota
                <InfoTip>{DEF.csat} Contada para quem encerrou.</InfoTip>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr
                key={a.userId}
                className="h-10 border-b border-divider last:border-0 hover:bg-accent/60"
              >
                <td className="max-w-48 truncate px-4 font-medium" title={a.userName}>
                  {a.userName}
                </td>
                <td className={TD_NUM}>{a.attended}</td>
                <td className={TD_NUM}>{a.closed}</td>
                <MetricCells m={a.firstResponse} />
                <MetricCells m={a.response} />
                <td className={TD_NUM}>{a.response.count}</td>
                <td className={TD_NUM}>{formatSeconds(a.service.avgSeconds)}</td>
                <td className={TD_NUM} title={`${a.csat.count} avaliações`}>
                  {csatValue(a.csat.avg)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Section>
  );
}

const chatHeaders = (client: string) => [
  'Conversa',
  'Entrada',
  'Contato',
  capitalize(client),
  'Mesa',
  'Atendentes',
  'Espera (min)',
  '1ª resposta (min)',
  'Atendimento (min)',
  'Encerrada',
  'Encerrada por',
  'Motivo',
  'Nota',
];

function chatCsvRow(c: AttendancePeriodChat, nowMs: number): (string | number)[] {
  return [
    c.chatId,
    formatWhen(c.enteredAt, nowMs),
    c.requestorName,
    c.clientName ?? '',
    c.deskName ?? '',
    c.attendants.join(', '),
    csvSeconds(c.waitSeconds),
    csvSeconds(c.firstResponseSeconds),
    csvSeconds(c.serviceSeconds),
    c.closedAt ? formatWhen(c.closedAt, nowMs) : '',
    c.closedByName ?? '',
    c.closeReasonName ?? (c.canceled ? 'Cancelada' : ''),
    c.csatScore ?? '',
  ];
}

function Chats({
  data,
  period,
}: {
  data: AttendancePeriod;
  period: { start: string; end: string };
}) {
  const [page, setPage] = useState(0);
  const terms = useTerms();
  const now = Date.now();
  const pages = Math.max(1, Math.ceil(data.chats.length / PAGE));
  const cur = Math.min(page, pages - 1);
  const slice = data.chats.slice(cur * PAGE, cur * PAGE + PAGE);
  return (
    <Section
      title="Conversas"
      count={data.chats.length}
      empty="Nenhuma conversa no período."
      hint="A mais recente primeiro"
      actions={
        data.chats.length > 0 ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={() =>
              downloadCsv(
                `atendimento-conversas-${period.start}-a-${period.end}.csv`,
                chatHeaders(terms.client),
                data.chats.map((c) => chatCsvRow(c, now))
              )
            }
          >
            <Download className="size-3.5" /> CSV
          </Button>
        ) : null
      }
    >
      {data.chats.length > 0 ? (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className={TH}>Entrada</th>
                <th className={TH}>Contato</th>
                <th className={TH}>Mesa</th>
                <th className={TH}>Atendentes</th>
                <th className={TH_NUM}>Espera</th>
                <th className={TH_NUM}>1ª resp.</th>
                <th className={TH_NUM}>Atend.</th>
                <th className={TH}>Encerramento</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((c) => (
                <tr
                  key={c.chatId}
                  className="h-10 border-b border-divider last:border-0 hover:bg-accent/60"
                >
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatWhen(c.enteredAt, now)}
                  </td>
                  <td className="max-w-52 px-4">
                    <Link
                      to="/chats"
                      search={{ chat: c.chatId }}
                      className="block truncate font-medium text-primary hover:underline"
                    >
                      {c.requestorName}
                    </Link>
                    {c.clientName ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {c.clientName}
                      </span>
                    ) : null}
                  </td>
                  <td className="max-w-36 truncate px-4 text-muted-foreground">
                    {c.deskName ?? '—'}
                  </td>
                  <td className="max-w-44 truncate px-4" title={c.attendants.join(', ')}>
                    {c.attendants.length > 0 ? (
                      c.attendants.join(', ')
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td
                    className={cn(
                      TD_NUM,
                      c.waitSeconds !== null && LEVEL_TEXT[waitLevel(c.waitSeconds)]
                    )}
                  >
                    {formatSeconds(c.waitSeconds)}
                  </td>
                  <td
                    className={cn(
                      TD_NUM,
                      c.firstResponseSeconds !== null &&
                        LEVEL_TEXT[waitLevel(c.firstResponseSeconds)]
                    )}
                  >
                    {formatSeconds(c.firstResponseSeconds)}
                  </td>
                  <td className={TD_NUM}>{formatSeconds(c.serviceSeconds)}</td>
                  <td className="max-w-52 px-4 text-[13px]">
                    {c.closedAt ? (
                      <span
                        className="block truncate"
                        title={[c.closedByName, c.closeReasonName].filter(Boolean).join(' · ')}
                      >
                        {formatWhen(c.closedAt, now)}
                        <span className="text-muted-foreground">
                          {' '}
                          · {c.closeReasonName ?? (c.canceled ? 'Cancelada' : 'sem motivo')}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">aberta</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between gap-2 border-t px-4 py-2 text-xs text-muted-foreground">
            <span>
              {cur * PAGE + 1}–{Math.min((cur + 1) * PAGE, data.chats.length)} de{' '}
              {data.chats.length}
              {data.chatsTruncated
                ? ' (a lista para aqui; os números acima cobrem o período inteiro)'
                : ''}
            </span>
            {pages > 1 ? (
              <span className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={cur === 0}
                  onClick={() => setPage(cur - 1)}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  disabled={cur >= pages - 1}
                  onClick={() => setPage(cur + 1)}
                >
                  Próxima
                </Button>
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </Section>
  );
}
